import {Bug, Landmark, Pencil, Settings, Trash2, Wallet} from 'lucide-react';
import {DateTime} from 'luxon';
import * as React from 'react';
import {useNavigate} from 'react-router-dom';
import {AccountRowV2} from 'src/AccountsView/AccountRowV2';
import {SectionCard} from 'src/AccountsView/AccountSection';
import {formatRelativeTime} from 'src/ConnectionsView/formatRelativeTime';
import {cn} from 'src/lib/utils';
import {LogoImage} from 'src/LogoImage';
import {isLogoId} from 'src/logoOptions';
import {useModals} from 'src/Modals';
import {PopoverMenu} from 'src/PopoverMenu';
import PopoverMenuItem from 'src/PopoverMenuItem';
import type {
  AccountListItemFragment,
  FullPlaidAccessTokenFragment,
  RecentSyncsQuery,
} from '@transactions/graphql';
import type {BalancesById} from 'src/AccountsView/accountBalances';

/** This connection's part of a sync session: when it ran, and how it went. */
export type ConnectionSync = {
  startedAt: string;
  result: RecentSyncsQuery['recentSyncs'][number]['results'][number];
};

type Props = {
  /** The Plaid connection, or null for the group of manual accounts. */
  token: FullPlaidAccessTokenFragment | null;
  accounts: AccountListItemFragment[];
  /** Balance per account id, streamed in separately from the metadata. */
  balances: BalancesById;
  /** The most recent sync that included this connection, if any is loaded. */
  lastSync?: ConnectionSync;
  /** Another connection shares this one's title, so show its id suffix. */
  showSuffix?: boolean;
  onRefetch: () => void;
};

/**
 * The name a connection goes by: its institution, or, for one Plaid never
 * named, the one account it links. Read from every linked account, not the
 * filtered rows, so a filter never renames it. The id suffix is the last
 * resort.
 */
export const connectionTitle = (token: FullPlaidAccessTokenFragment): string =>
  token.institutionName ??
  (token.accounts.length === 1
    ? token.accounts[0].name
    : `•••${token.tokenSuffix}`);

type Health = {tone: 'good' | 'warn' | 'bad' | 'none'; label: string | null};

/**
 * What the status line leads with. A connection Plaid has turned off, or
 * one whose last sync asked for a relink, needs the user; a failed sync is
 * worth a look; otherwise the line just says when it last synced.
 */
const healthOf = (
  token: FullPlaidAccessTokenFragment,
  lastSync: ConnectionSync | undefined,
  relinkedAt: DateTime | null,
): Health => {
  if (!token.active) {
    return {tone: 'bad', label: 'Disconnected'};
  }
  // A Plaid Link completed since the last sync supersedes that sync's
  // verdict, which is history until the next sync runs.
  if (
    relinkedAt != null &&
    (lastSync == null || DateTime.fromISO(lastSync.startedAt) < relinkedAt)
  ) {
    return {tone: 'good', label: 'Relinked'};
  }
  if (lastSync?.result.needsRelink) {
    return {tone: 'bad', label: 'Needs relink'};
  }
  if (lastSync && lastSync.result.status !== 'success') {
    return {tone: 'warn', label: 'Sync failed'};
  }
  if (lastSync) {
    const when = formatRelativeTime(lastSync.startedAt);
    // Mid-sentence, the words lowercase; a date ("Jul 3") keeps its capital.
    const inSentence =
      when === 'Yesterday' || when === 'Just now' ? when.toLowerCase() : when;
    return {tone: 'good', label: `Synced ${inSentence}`};
  }
  return {tone: 'none', label: null};
};

const DOT_CLASS: Record<Health['tone'], string> = {
  good: 'bg-green-600 dark:bg-green-500',
  warn: 'bg-amber-500',
  bad: 'bg-destructive',
  none: 'bg-muted-foreground/40',
};

// A problem reads in its color; a healthy line stays quiet.
const LABEL_CLASS: Record<Health['tone'], string> = {
  good: '',
  warn: 'text-amber-600 dark:text-amber-500',
  bad: 'text-destructive',
  none: '',
};

/**
 * One connection as a card: the institution and its health in the header,
 * its accounts beneath. Manual accounts get the same card with a plain
 * header. No subtotal: a connection mixes assets and debts, and the money
 * view is the Accounts page.
 */
export const ConnectionGroup = ({
  token,
  accounts,
  balances,
  lastSync,
  showSuffix = false,
  onRefetch,
}: Props) => {
  const {editPlaidTokenModal, deletePlaidTokenModal, manageConnectionModal} =
    useModals();
  const navigate = useNavigate();
  const [relinkedAt, setRelinkedAt] = React.useState<DateTime | null>(null);

  const handleManageConnection = () => {
    if (!token) return;
    manageConnectionModal.openModal({
      plaidAccessTokenId: token._id,
      institutionName: token.institutionName,
      currentProducts: token.products,
      // Fires only after Plaid Link succeeds, which reauthenticates.
      onSuccess: () => {
        setRelinkedAt(DateTime.now());
        onRefetch();
      },
    });
  };

  const handleEditToken = () => {
    if (token) {
      editPlaidTokenModal.openModal({token, onSuccess: onRefetch});
    }
  };

  const handleDeleteToken = () => {
    if (token) {
      deletePlaidTokenModal.openModal({token, onDeleted: onRefetch});
    }
  };

  // The institution wears the logo its accounts already carry.
  const logo = token
    ? accounts.find((account) => isLogoId(account.logo))?.logo
    : null;
  const title = token ? connectionTitle(token) : 'Manual';
  const health = token ? healthOf(token, lastSync, relinkedAt) : null;
  const details = token
    ? [
        token.environment === 'production' ? null : 'Development',
        showSuffix ? `•••${token.tokenSuffix}` : null,
      ].filter((part) => part != null)
    : [];

  return (
    <SectionCard>
      <div className="bg-muted/40 flex items-center gap-3 px-3 py-3 sm:px-4">
        <span className="flex w-10 shrink-0 justify-center">
          {isLogoId(logo) ? (
            <LogoImage logo={logo} rounded="rounded-lg" className="size-8" />
          ) : (
            <span className="bg-muted text-muted-foreground flex size-8 items-center justify-center rounded-lg">
              {token ? (
                <Landmark className="size-4" />
              ) : (
                <Wallet className="size-4" />
              )}
            </span>
          )}
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="text-foreground truncate font-semibold">
              {title}
            </span>
            <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
              {accounts.length}
            </span>
          </span>
          {token && health && (
            <span className="text-muted-foreground flex min-w-0 items-center gap-1.5 text-xs">
              <span
                aria-hidden="true"
                className={cn('size-1.5 shrink-0 rounded-full', DOT_CLASS[health.tone])}
              />
              <span className="truncate">
                {health.label != null && (
                  <span className={LABEL_CLASS[health.tone]}>
                    {health.label}
                  </span>
                )}
                {health.label != null && details.length > 0 && ' · '}
                {details.join(' · ')}
              </span>
            </span>
          )}
        </div>
        {token && (
          <div className="-mr-1">
            <PopoverMenu>
              <PopoverMenuItem iconLeft={<Pencil />} onSelect={handleEditToken}>
                Edit Connection
              </PopoverMenuItem>
              <PopoverMenuItem
                iconLeft={<Settings />}
                onSelect={handleManageConnection}
              >
                Manage Connection
              </PopoverMenuItem>
              <PopoverMenuItem
                iconLeft={<Bug />}
                onSelect={() => navigate(`/connections/${token._id}/debug`)}
              >
                Debug Sync
              </PopoverMenuItem>
              <PopoverMenuItem
                iconLeft={<Trash2 className="text-destructive" />}
                onSelect={handleDeleteToken}
                className="text-destructive"
              >
                Delete Connection
              </PopoverMenuItem>
            </PopoverMenu>
          </div>
        )}
      </div>
      {accounts.length > 0 ? (
          accounts.map((account) => (
            <AccountRowV2
              key={account._id}
              account={account}
              balance={balances.get(account._id)}
            />
          ))
        ) : (
          <div className="text-muted-foreground px-4 py-3 text-sm">
            No accounts
          </div>
        )}
    </SectionCard>
  );
};
